import type { ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * O número com o rótulo em versalete — o MIOLO de quem publica um número.
 *
 * Ele não desenha caixa nenhuma, e é por isso que continua existindo depois de
 * `StatCard`: quem tem casca é o cartão, e o que vai dentro dele é isto. Os
 * três lugares que o usam hoje são o próprio `StatCard`, o cartão de meta do
 * comercial e o boleto em destaque do portal — os dois últimos porque a caixa
 * deles já existe e tem outra forma.
 *
 * ## O defeito que isto conserta
 *
 * Quatro painéis do início — demandas, retenção, cobrança e comercial —
 * declaravam cada um o seu, privado, com o mesmo `<dt className="rotulo">` e o
 * mesmo `<dd className="mt-0.5">`. E divergiam onde ninguém olharia:
 *
 * | painel    | nome     | tamanho    | esqueleto     |
 * | --------- | -------- | ---------- | ------------- |
 * | demandas  | `Number` | `text-2xl` | `h-7 w-10`    |
 * | retenção  | `Number` | `text-2xl` | `h-7 w-10`    |
 * | cobrança  | `Metric` | `text-xl`  | `h-7 w-24`    |
 * | comercial | `Metric` | `text-xl`  | `h-7 w-24`    |
 *
 * Os quatro painéis aparecem **empilhados na mesma tela de início**. Medido no
 * navegador em 01/09/2026: `24px` no de demandas e `20px` no de cobrança, um
 * abaixo do outro. Ninguém escolheu isso; foi o que sobrou de quatro cópias.
 *
 * O tamanho aqui é `text-xl`. O maior perdeu porque o número grande com rótulo
 * pequeno é o clichê que o `DESIGN.md` bane, e num painel de remuneração ele
 * seria o pior lugar para exibir número que ninguém apurou.
 *
 * ## O esqueleto não é enfeite
 *
 * Enquanto a consulta está no ar, o lugar do número é um esqueleto e **nunca
 * um zero**. Zero é um fato; afirmá-lo antes de saber é a tela mentindo com
 * aparência de precisão. Quem passa `value === undefined` recebe o esqueleto —
 * essa é a única forma de dizer "ainda não sei" aqui dentro.
 */
export function Metric({
  label,
  value,
  support,
  tone,
  surface = 'clara',
  labelTone = 'apoio',
  className,
}: {
  label: string;
  /** `undefined` é "ainda não sei", e vira esqueleto. Nunca passe zero por ele. */
  value: ReactNode | undefined;
  /** A linha de apoio abaixo do número — "3 boletos", "de 12 no mês". */
  support?: ReactNode;
  /** `erro` só quando o número em si é a má notícia: vencidos, atrasados. */
  tone?: 'erro';
  /**
   * Em que fundo esta métrica vive — e ela precisa saber, porque o rótulo e a
   * linha de apoio escrevem cor.
   *
   * `texto-suave` é a cor de apoio sobre superfície CLARA: dentro de um bloco
   * `bg-chrome` ela sai a ~2,4:1 contra o navy, que é ilegível. `data-chrome`
   * não conserta isso — ele remapeia o anel de foco e a barra de rolagem, não a
   * paleta de texto. O valor não entra na conta: ele não declara cor e herda a
   * do bloco.
   */
  surface?: 'clara' | 'chrome';
  /**
   * `marca` escreve o RÓTULO no laranja da casa (`primaria-texto`, o tom do
   * laranja que passa como letra) — o boleto do portal, por pedido do dono em
   * 23/09/2026.
   *
   * Mora aqui, e não num `text-primaria-texto` escrito na tela, porque a
   * decisão 5 do `DESIGN.md` lê primária solta numa tela como AÇÃO, e a régua
   * `primaria-solta` a reprova. Um rótulo de valor não é clicável; ser uma
   * opção deste componente é o que o separa de um link. Só vale em superfície
   * clara: sobre o chrome o rótulo continua a cor de apoio do bloco.
   */
  labelTone?: 'apoio' | 'marca';
  className?: string;
}) {
  const support_ = surface === 'chrome' ? 'text-chrome-texto-suave' : 'text-texto-suave';

  return (
    <div className={className}>
      <dt
        className={cn(
          'rotulo',
          labelTone === 'marca' && surface === 'clara' ? 'text-primaria-texto' : support_,
        )}
      >
        {label}
      </dt>
      <dd className="mt-0.5">
        {value === undefined ? (
          <>
            <span className="esqueleto block h-7 w-20" />
            {/*
             * A segunda barra aparece se `support` foi PASSADO, mesmo vazio —
             * quem tem linha de apoio a tem carregada ou não, e um esqueleto de
             * uma barra só faria o cartão mudar de altura quando o dado chegasse.
             */}
            {support !== undefined && <span className="esqueleto mt-1 block h-3 w-24" />}
          </>
        ) : (
          <>
            <span
              className={cn(
                'block text-xl font-bold tabular-nums',
                tone === 'erro' && 'text-erro',
              )}
            >
              {value}
            </span>
            {support && (
              <span className={cn('mt-0.5 block text-xs', support_)}>{support}</span>
            )}
          </>
        )}
      </dd>
    </div>
  );
}
